<template>
  <Record
    class="RecordLaptop"
    :class="{ isChecked }"
    :title="laptop.name">
    <div class="recordRow">
      <slot name="recordLeftSide" />
      <div>
        <Row>
          <Column :lg="3">
            <LaptopImage :laptop="laptop" />
          </Column>
          <Column :lg="9">
            <Row>
              <Column
                v-for="{ label, value, key } in laptopSpecs"
                :key="label"
                :md="4"
                :xs="6">
                <SetSpec
                  :label="label"
                  :laptop="laptop"
                  :specKey="key">
                  {{ value ? value : '-' }}
                </SetSpec>
              </Column>
              <Set icon="link" :label="laptop.asin">
                <a :href="formAmazonLink(laptop.asin)">
                  {{ $t('link') }}
                </a>
              </Set>
            </Row>
          </Column>
        </Row>
      </div>
    </div>
    <div v-if="!hideActions" slot="actions" class="actionsWithTags">
      <RecordActions
        v-if="hasRole('>=MODERATOR')"
        :doShowDelete="hasRole('>=ADMIN')"
        name="laptop"
        :record="laptop"
        routeName="Laptop">
        <Btn class="ib sm inv noBorder" icon="file-copy-alt" @click="copy">
          {{ $t('copy') }}
        </Btn>
        <Btn
          class="ib sm inv noBorder"
          :isLoading="isPatching"
          @click="toggleIsArchived">
          {{ laptop.isArchived ? $t('unarchive') : $t('archive') }}
        </Btn>
      </RecordActions>
      <LaptopTags
        slot="actions"
        :laptop="laptop" />
    </div>
  </Record>
</template>

<script>
import { actions, getters } from '@utils'
import { filter, pick, pipe } from 'rambda'
import LaptopImage from '@components/LaptopImage'
import LaptopTags from '@components/LaptopTags'
import RecordActions from '@components/RecordActions'
import SetSpec from './SetSpec'
import { isNotNil } from '@utils/guards'

export default {
  components: {
    LaptopImage,
    LaptopTags,
    RecordActions,
    SetSpec,
  },
  props: {
    isChecked: Boolean,
    hideActions: Boolean,
    laptop: Object,
  },
  data() {
    return {
      isPatching: false,
    }
  },
  computed: {
    laptopSpecs() {
      const { laptop } = this
      const { currency, fallback } = this.$options.filters
      const dollars = price => price
        ? currency(price, '$', 0)
        : fallback(price)
      const suffix = (value, suffix) => value
        ? `${value} ${suffix}`
        : fallback()

      return [
        {
          key: 'price',
          label: this.$t('price'),
          score: laptop.valueScore,
          value: dollars(laptop.price),
        },
        {
          key: 'cpu',
          label: this.$t('specTypes.cpu'),
          score: laptop.computingScore,
          scoreClass: 'inv',
          value: laptop.cpu,
        },
        {
          key: 'gpu',
          label: this.$t('specTypes.graphics'),
          score: laptop.graphicsScore,
          scoreClass: 'inv',
          value: laptop.gpu,
        },
        {
          key: 'memory',
          label: this.$t('specTypes.memory'),
          score: laptop.memoryScore,
          scoreClass: 'inv',
          value: laptop.memoryAmount,
        },
        {
          key: 'storage',
          label: this.$t('specTypes.storage'),
          score: laptop.storageScore,
          scoreClass: 'inv',
          value: [
            [laptop.ssdAmount, ' GB SSD'],
            [laptop.hddAmount, ' GB HDD'],
          ].filter(([amount]) => amount)
            .map(amountWithSuffix => amountWithSuffix.join(''))
            .join('; '),
        },
        {
          key: 'screen',
          label: this.$t('specTypes.screen'),
          score: laptop.screenScore + laptop.screenQualityScore,
          scoreClass: 'inv',
          value: [
            laptop.screen,
            laptop.resolution,
            laptop.screenNits,
            laptop.screenContrast,
            laptop.screenP3,
            laptop.screenSrgb,
            laptop.screenRgb,
          ].filter(Boolean).join('; '),
        },
        {
          key: 'weight',
          label: this.$t('specTypes.weight'),
          score: laptop.weightScore,
          scoreClass: 'inv',
          value: suffix(laptop.weight, 'kg'),
        },
        {
          key: 'battery',
          label: this.$t('specTypes.battery'),
          score: laptop.batteryScore,
          scoreClass: 'inv',
          value: suffix(laptop.batteryMinutes, 'min'),
        },
        {
          key: 'ports',
          label: this.$t('specTypes.ports'),
          score: laptop.portsScore,
          scoreClass: 'inv',
          value: [
            [laptop.portsHdmi, this.$t('ports.hdmi')],
            [laptop.portsDisplayPort, this.$t('ports.displayPort')],
            [laptop.portsThunderbolt, this.$t('ports.thunderbolt')],
            [laptop.portsUsb3c, this.$t('ports.usb3c')],
            [laptop.portsUsb3a, this.$t('ports.usb3a')],
            [laptop.portsUsb2, this.$t('ports.usb2')],
          ].filter(([portCount]) => portCount)
            .map(port => port.join(' '))
            .join(', '),
        },
      ]
    },
    ...getters('formAmazonLink'),
  },
  methods: {
    copy() {
      this.$clipboard(JSON.stringify(this.laptop))
      this.SUCCESS(this.$t('misc.copied'))
    },
    patch(data) {
      return this.$wrap(
        () => this.PATCH_LAPTOP({
          id: this.laptop.id,
          ...data,
        }),
        'isPatching',
      )
    },
    pasteScreenSpecs(laptop) {
      const data = pipe(
        pick([
          'batteryCells',
          'batteryMinutes',
          'batteryWh',

          'hasBacklight',
          'hasNumpad',
          'hasNvme',

          'portsDisplayPort',
          'portsHdmi',
          'portsThunderbolt',
          'portsUsb2',
          'portsUsb3a',
          'portsUsb3c',
          'portsUsb4',

          'screenContrast',
          'screenNits',
          'screenP3',
          'screenRgb',
          'screenSrgb',

          'linksExternal',
          'reviewsExternal',

          'bonus',
          'weight',
        ]),
        filter(isNotNil),
      )(laptop)

      return this.patch(data)
    },
    toggleIsArchived() {
      return this.patch({
        isArchived: !this.laptop.isArchived,
      })
    },
    ...actions('PATCH_LAPTOP'),
  },
  async beforeMount() {
    this.$offEvents = await this.ON({
      paste: (event) => {
        if (!this.$el.matches(':hover')) return

        event.preventDefault()

        const data = JSON.parse(event.clipboardData.getData('text/plain'))

        return this.pasteScreenSpecs(data)
      },
    })
  },
  beforeDestroy() {
    this.$offEvents && this.$offEvents()
  },
}
</script>

<style lang="scss">
.RecordLaptop {
  .recordRow {
    display: flex;
  }

  .image {
    img {
      width: 100%;
    }
  }

  .actionsWithTags {
    display: flex;
    justify-content: space-between;

    .Tag {
      margin-left: 0.5rem;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "archive": "Archive",
    "unarchive": "Unarchive",
    "copy": "Copy",
    "pasteScreenSpecs": "Paste screen specs",
    "link": "View on Amazon",
    "ports": {
      "hdmi": "HDMI",
      "displayPort": "DisplayPort",
      "thunderbolt": "Thunderbolt",
      "usb3c": "USB 3C",
      "usb3a": "USB 3A",
      "usb2": "USB 2"
    },
    "price": "Price",
    "under": "Under {0}"
  }
}
</i18n>
